import { useState, useEffect } from "react";
import {
  loadWalmartStores, loadHomeDepotStores, loadDollarGeneralStores, loadTargetStores, loadBestBuyStores, loadCostcoStores, loadLowesStores, loadTractorSupplyStores, loadNorthernToolStores, loadSamsClubStores,
} from "../utils/storesLoader";
import { isOnlineOnlyRetailer, type RetailerType } from "../config/retailers";
import {
  type WalmartStore, type HomeDepotStore, type DollarGeneralStore, type TargetStore, type BestBuyStore, type CostcoStore, type LowesStore, type TractorSupplyStore, type LocalClearanceStore,
} from "../utils/storeUtils";

export type { TargetStore, BestBuyStore, CostcoStore, LowesStore, TractorSupplyStore, LocalClearanceStore };
export type StoreData = WalmartStore[] | HomeDepotStore[] | DollarGeneralStore[] | TargetStore[] | BestBuyStore[] | CostcoStore[] | LowesStore[] | TractorSupplyStore[] | LocalClearanceStore[];

/**
 * Hook to load stores for a specific retailer from S3
 * @param retailer - The retailer type
 * @param forceRefresh - Force refresh even if cache is valid
 * @returns Object with stores data, loading state, and error
 */
export function useStores<T extends StoreData = StoreData>(
  retailer: RetailerType,
  forceRefresh = false
) {
  const [stores, setStores] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadStores = async () => {
      setLoading(true);
      setError(null);

      try {
        let loadedStores: T;
        if (isOnlineOnlyRetailer(retailer)) {
          loadedStores = [] as unknown as T;
        } else {
        switch (retailer) {
          case "walmart":
            loadedStores = (await loadWalmartStores<WalmartStore[]>(
              forceRefresh
            )) as T;
            break;
          case "home_depot":
            loadedStores = (await loadHomeDepotStores<HomeDepotStore[]>(
              forceRefresh
            )) as T;
            break;
          case "dollar_general":
            loadedStores = (await loadDollarGeneralStores<DollarGeneralStore[]>(
              forceRefresh
            )) as T;
            break;
          case "cvs":
            // CVS store discovery is server-side and ZIP-driven. There is no
            // selectable store catalog to download into the browser.
            loadedStores = [] as unknown as T;
            break;
          case "target":
            loadedStores = (await loadTargetStores<TargetStore[]>(
              forceRefresh
            )) as T;
            break;
          case "bestbuy":
            loadedStores = (await loadBestBuyStores<BestBuyStore[]>(
              forceRefresh
            )) as T;
            break;
          case "costco":
            loadedStores = (await loadCostcoStores<CostcoStore[]>(
              forceRefresh
            )) as T;
            break;
          case "lowes":
            loadedStores = (await loadLowesStores<LowesStore[]>(
              forceRefresh
            )) as T;
            break;
          case "tractor_supply":
            loadedStores = (await loadTractorSupplyStores<TractorSupplyStore[]>(
              forceRefresh
            )) as T;
            break;
          case "northern_tool":
            loadedStores = (await loadNorthernToolStores<LocalClearanceStore[]>(
              forceRefresh
            )) as T;
            break;
          case "samsclub":
            loadedStores = (await loadSamsClubStores<LocalClearanceStore[]>(
              forceRefresh
            )) as T;
            break;
          default:
            throw new Error(`Unknown retailer: ${retailer}`);
        }
        }

        if (!cancelled) {
          setStores(loadedStores);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setLoading(false);
        }
      }
    };

    loadStores();

    return () => {
      cancelled = true;
    };
  }, [retailer, forceRefresh]);

  return { stores, loading, error };
}
